import type { PaymentMethodVerificationStatusUpdateMessage } from '@/queries/user/user.schema';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import Toast from '@/components/ui/Toast/toast';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import openModal from '@/modals/utils/openModal';
import { paymentMethodsQueryKey } from '@/queries/user/user.queries';
import { useAppSelector } from '@/redux/hooks';

interface PaymentMethodVerificationRequiredMessage {
  userId: string;
  paymentMethodId: string;
}

export function usePaymentMethodVerificationRequiredSocket() {
  const userSession = useAppSelector(state => state.user.userSession);

  useSocketEvent<PaymentMethodVerificationRequiredMessage>(
    'payment-method.verification-required',
    (data) => {
      openModal('card-verify-request', { paymentMethodId: data.paymentMethodId });
    },
    Boolean(userSession),
  );
}

export function useUserSocket() {
  const userSession = useAppSelector(state => state.user.userSession);
  const t = useTranslations('payment-modals.card-state');
  const queryClient = useQueryClient();

  useSocketEvent<PaymentMethodVerificationStatusUpdateMessage>(
    'payment-method-verification.status.update',
    (data) => {
      queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });

      if (data.state === 'approved') {
        Toast({
          type: 'success',
          title: t('card-verification-complete'),
          description: t('ready-for-redemptions'),
        });
        return;
      }

      if (data.state === 'rejected') {
        Toast({
          type: 'error',
          title: t('reject.title'),
          disableTimer: true,
          description: t('reject.description'),
          acceptButtonText: t('reject.accept-button'),
          rejectButtonText: t('reject.reject-button'),
          onAccept: () => {
            window.Intercom?.('show');
          },
        });
      }
    },
    Boolean(userSession),
  );
}
